import React, {useEffect} from 'react';
import {VideoEditorState} from '../store/VideoEditorStore';
import styles from './ChunkListOverlay.module.css';
import {ReactComponent as RegenerateIcon} from '../../../assets/images/regenerate-icon.svg';
import {ReactComponent as MuteAudioIcon} from '../../../assets/images/mute-icon.svg';
import {ReactComponent as ChangeMediaIcon} from '../../../assets/images/changeMedia.svg';
import {ReactComponent as EditIcon} from '../../../assets/images/edit.svg';
import {ReactComponent as AIVideoIcon} from '../../../assets/images/ai-video-menu-icon.svg';
import {VideoEditorText} from '../VideoEditorText';
import LoadingSpinner from '../../../uiToolkit/LoadingSpinner/LoadingSpinner';

interface Props {
  state: VideoEditorState;
  isLoading: boolean;
  spinTrigger: number;
  refreshChunks: () => void;
  isEditingTemporaryDisabled?: boolean;
}

const ChunkListOverlay: React.FC<Props> = ({
  state,
  isLoading,
  spinTrigger,
  refreshChunks,
  isEditingTemporaryDisabled,
}: Props) => {
  const spinnerRef = React.useRef<SVGSVGElement | null>(null);

  useEffect(() => {
    if (spinnerRef.current) {
      const spinner = spinnerRef.current;
      spinner.classList.remove(styles.spinOnce);
      requestAnimationFrame(() => {
        spinner.classList.add(styles.spinOnce);
      });
    }
  }, [spinTrigger]);

  const getLoadingText = (
    state: VideoEditorState,
    editingDisabled?: boolean,
  ) => {
    if (editingDisabled && state === VideoEditorState.Idle) {
      return VideoEditorText.stateRegenerating;
    }
    if (state === VideoEditorState.ChangingMedia) {
      return VideoEditorText.stateChangingMedia;
    }
    if (
      state === VideoEditorState.ReplacingWithLibraryVideo ||
      state === VideoEditorState.ReplacingWithLibraryImage
    ) {
      return VideoEditorText.stateReplacingWithLibrary;
    }
    if (state === VideoEditorState.Regenerating) {
      return VideoEditorText.stateRegenerating;
    }
    if (state === VideoEditorState.MutingAudio) {
      return VideoEditorText.stateMutingAudio;
    }
    if (state === VideoEditorState.ChangingNarration) {
      return VideoEditorText.stateChangingNarration;
    }
    if (state === VideoEditorState.ReplacingAIMedia) {
      return VideoEditorText.stateReplacingAIMedia;
    }
    if (state === VideoEditorState.DisablingBackgroundAudio) {
      return VideoEditorText.stateDisablingBackgroundAudio;
    }
    return '';
  };

  const getSpinnerIcon = (
    state: VideoEditorState,
    editingDisabled?: boolean,
  ) => {
    let spinnerIcon;

    if (state === VideoEditorState.Regenerating) {
      spinnerIcon = <RegenerateIcon ref={spinnerRef} />;
    } else if (editingDisabled && state === VideoEditorState.Idle) {
      spinnerIcon = <RegenerateIcon ref={spinnerRef} />;
    } else if (state === VideoEditorState.MutingAudio) {
      spinnerIcon = <MuteAudioIcon ref={spinnerRef} />;
    } else if (
      state === VideoEditorState.ChangingMedia ||
      state === VideoEditorState.ReplacingWithLibraryVideo ||
      state === VideoEditorState.ReplacingWithLibraryImage
    ) {
      spinnerIcon = <ChangeMediaIcon ref={spinnerRef} />;
    } else if (state === VideoEditorState.ChangingNarration) {
      spinnerIcon = <EditIcon ref={spinnerRef} />;
    } else if (state === VideoEditorState.ReplacingAIMedia) {
      spinnerIcon = <AIVideoIcon ref={spinnerRef} />;
    } else if (state === VideoEditorState.DisablingBackgroundAudio) {
      spinnerIcon = <MuteAudioIcon ref={spinnerRef} />;
    } else {
      spinnerIcon = <LoadingSpinner size={24} />;
    }

    return <span>{spinnerIcon}</span>;
  };

  return (
    <div>
      {(isLoading ||
        state !== VideoEditorState.Idle ||
        isEditingTemporaryDisabled) && (
        <div className={styles.overlay}>
          <span className={styles.loadingText} onClick={refreshChunks}>
            {getSpinnerIcon(state, isEditingTemporaryDisabled)}
            {getLoadingText(state, isEditingTemporaryDisabled)}
          </span>
        </div>
      )}
    </div>
  );
};
export default ChunkListOverlay;
